import { POLICY_DOCUMENT_TYPES, UPLOADABLE_DOCUMENT_TYPES } from '../../resources/enums';
import { IVehicleType, IVehicleUsages } from '../../resources/interfaces';
import { getVehicleUsageFromKey } from '../../utils/miscFunctions';
import { Driver } from '../driver/Driver';
import SupportedVehicle from '../SupportedVehicle';
import { IVehicle } from './IVehicle';
import { IVehicleImage, IVehicleInspectionData } from './IVehicle';

export class Vehicle implements SupportedVehicle {
  ref: string;
  id: string;
  verified: boolean;
  vehicleAltered: boolean | undefined;
  alterationDetails?: string;
  analysisSummary?: string;
  goodRepairState: boolean | undefined;
  repairState: string;
  color?: string;
  city?: string;
  region: string | undefined;
  driver: Driver;
  make: string | undefined;
  maker: string | undefined;
  model: string;
  type: IVehicleType;
  yearIssued: number;
  maxSeat: number;
  miniSeat: number;
  avatar: string | undefined;
  cc: number | undefined;
  ownerName: string | undefined;
  ownerAddress: string | undefined;
  ownerPhoneNumber: string;
  usage: IVehicleUsages | null;
  vehicleUsage: string | null;
  registrationYear: number | undefined;
  insuredValue: number | undefined;
  chassisNumber: string;
  registrationNumber: string | undefined;
  manufacturedYear: number | undefined;
  images: IVehicleImage[] | undefined;
  hasInterestedParties: boolean | undefined;
  interestedParties: string;
  hasInstalledAccessories: boolean | undefined;
  installedAccessoriesDetails?: string;
  installedAccessoriesValue: number;
  natureOfInterest?: string;
  inspectionData: IVehicleInspectionData[] | undefined;
  state: string | undefined;
  score: number | undefined;



  constructor(Imodel: IVehicle) {
    this.ref = Imodel.ref;
    this.id = Imodel.id;
    this.vehicleAltered = Imodel.vehicleAltered ?? undefined;
    this.alterationDetails = Imodel.alterationDetails ?? undefined;
    this.goodRepairState = Imodel.vehicleInGoodRepairState  || Imodel.isInGoodRepairState || undefined;
    this.repairState = Imodel.repairState ?? undefined;
    this.color = Imodel.color ?? undefined;
    this.city = Imodel.city ?? undefined;
    this.region = Imodel.region ?? undefined;
    this.driver = new Driver({
      fullName: Imodel.driverName,
      phoneNumber: Imodel.driverPhoneNumber,
      email: Imodel.driverEmailAddress,
      license: Imodel.driverLicense,
      hasDisease: Imodel.driverHasComplications ?? undefined,
      diseaseOrComplications: Imodel.driverComplications,
      driverComplications: Imodel.driverComplications ?? '',
    });
    this.make = Imodel.maker ?? Imodel.make ?? '';
    this.maker = Imodel.maker ?? '';
    this.model = Imodel.model ?? '';
    this.type = Imodel.type ?? { name: '', description: '' };
    this.yearIssued = Imodel.yearIssued ?? 0;
    this.maxSeat = Imodel.maxSeat ?? 1;
    this.miniSeat = Imodel.miniSeat ?? 1;
    this.cc = Imodel.cc;
    this.ownerName = Imodel.ownerName;
    this.ownerAddress = Imodel.ownerAddress;
    this.ownerPhoneNumber = Imodel.ownerPhoneNumber ?? '';
    this.usage = getVehicleUsageFromKey(Imodel.vehicleUsage ?? '');
    this.vehicleUsage = Imodel.vehicleUsage ?? '';
    this.registrationYear = Imodel.registrationYear;
    this.manufacturedYear = Imodel.manufacturedYear ?? Imodel.yearIssued ?? 0;
    // Prefer the legacy issuedValue when non-zero (existing consumers
    // still write there); fall back to sumInsured (the new per-vehicle
    // column on the fleet redesign) so redesigned fleets show the
    // correct number in the Issued Value column even before the write
    // side migrates fully. A raw 0 in issuedValue is treated as
    // "unpopulated" so the sumInsured fallback still kicks in.
    this.insuredValue = Imodel.issuedValue && Imodel.issuedValue > 0
      ? Imodel.issuedValue
      : (Imodel as any).sumInsured;
    this.chassisNumber = Imodel.chassisNumber;
    this.registrationNumber = Imodel.registrationNumber;
    this.images = Imodel.vehicleDocuments?.map((image: IVehicleImage) => ({
      docType: image.docType,
      docUrl: image.docUrl || image.docURL,
      docURL: image.docURL,
      id: image.id,
      title: image.title
    }));
    this.verified = Imodel.verified;
    this.hasInterestedParties = Imodel.hasInterestedParties ?? undefined;
    this.interestedParties = Imodel.interestedParties ?? '';
    this.hasInstalledAccessories = Imodel.hasInstalledAccessories ?? undefined;
    this.installedAccessoriesDetails = Imodel.installedAccessoriesDetails;
    this.installedAccessoriesValue = Imodel.installedAccessoriesValue ?? 0;
    this.natureOfInterest = Imodel.natureOfInterest ?? undefined;
    this.inspectionData = Imodel.inspectionData ?? undefined;
    this.state = Imodel.state ?? undefined;
    this.score = Imodel.score ?? undefined;
    this.analysisSummary = Imodel.analysisSummary ?? undefined;
  }

  getDisplayName(): string {
    return `${this.model} - ${this.type.name}`;
  }

  getDetailedName(): string {
    return `${this.make} ${this.model} - ${this.type.name}`;
  }

  getFrontPhoto(): IVehicleImage | undefined {
    return this.images?.find(
      (image: IVehicleImage) => image.docType === UPLOADABLE_DOCUMENT_TYPES.VEHICLE_PHOTO_FRONT
    );
  }

  getBackPhoto(): IVehicleImage | undefined {
    return this.images?.find(
      (image: IVehicleImage) => image.docType === UPLOADABLE_DOCUMENT_TYPES.VEHICLE_PHOTO_BACK
    );
  }

  getRightPhoto(): IVehicleImage | undefined {
    return this.images?.find(
      (image: IVehicleImage) => image.docType === UPLOADABLE_DOCUMENT_TYPES.VEHICLE_PHOTO_RIGHT
    );
  }

  getLeftPhoto(): IVehicleImage | undefined {
    return this.images?.find(
      (image: IVehicleImage) => image.docType === UPLOADABLE_DOCUMENT_TYPES.VEHICLE_PHOTO_LEFT
    );
  }
}


